import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type {
  ConnectorActionDetail,
  ConnectorOperation,
  ConnectorServiceActions,
  InstalledPlugin,
  LocalPluginPreview,
  PluginCatalogItem,
  PluginMcpServer,
  PluginRpcRequest,
  PluginSourceWriteResult,
} from '@crowdex/plugin-contracts';

export type McpServerStatus = {
  serverId: string;
  state: 'connecting' | 'connected' | 'reconnecting' | 'error' | 'closed';
  error?: string;
  discovery?: {
    supportedVersions: string[];
    instructions?: string;
    capabilities: Record<string, unknown>;
  };
};

/** Calls `listener` when the user changes plugins (installed, enabled, configured). */
export function onPluginsChanged(listener: () => void): () => void {
  window.addEventListener('crowdex-plugins-changed', listener);
  return () => window.removeEventListener('crowdex-plugins-changed', listener);
}

const pendingInventories = new Map<string | undefined, Promise<InstalledPlugin[]>>();

function listInstalledPlugins(workspaceRoot?: string): Promise<InstalledPlugin[]> {
  const pending = pendingInventories.get(workspaceRoot);
  if (pending !== undefined) return pending;
  const loading = request<InstalledPlugin[]>({
    method: 'installed.list',
    params: workspaceRoot === undefined ? {} : { workspaceRoot },
  });
  pendingInventories.set(workspaceRoot, loading);
  const release = () => {
    if (pendingInventories.get(workspaceRoot) === loading) pendingInventories.delete(workspaceRoot);
  };
  void loading.then(release, release);
  return loading;
}

/**
 * The plugin API of the server this app runs on: the account's plugins in the
 * cloud, the machine's plugins (hosted by its workspace server) locally.
 */
async function request<TResult>(request: PluginRpcRequest): Promise<TResult> {
  const result = await apiFetch<TResult>('/api/plugins/manage', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(request),
  });
  if (
    [
      'settings.update',
      'install',
      'local.install',
      'uninstall',
      'skills.machine.remove',
      'enabled.set',
      'contributions.enabled.set',
      'commands.unsandboxed.set',
      'source.write',
      'update.approve',
      'publish',
    ].includes(request.method)
  ) {
    pendingInventories.clear();
    window.dispatchEvent(new Event('crowdex-plugins-changed'));
  }
  return result;
}

export const nativePluginClient = {
  settingsProjects: (workspaceRoot?: string) =>
    request<{ id: string; name: string }[]>({
      method: 'settings.projects',
      params: { workspaceRoot },
    }),
  getSettings: (name: string, project?: string, workspaceRoot?: string) =>
    request<import('@crowdex/plugin-contracts').PluginSettingsSnapshot>({
      method: 'settings.get',
      params: { name, project, workspaceRoot },
    }),
  updateSettings: (
    name: string,
    patch: import('@crowdex/plugin-contracts').PluginSettingsPatch,
    project?: string,
    workspaceRoot?: string,
  ) =>
    request<import('@crowdex/plugin-contracts').PluginSettingsSnapshot>({
      method: 'settings.update',
      params: { name, patch, project, workspaceRoot },
    }),
  subscribeSettings: (
    name: string,
    listener: (snapshot: import('@crowdex/plugin-contracts').PluginSettingsSnapshot) => void,
    options: { project?: string; workspaceRoot?: string; onError?: (error: unknown) => void } = {},
  ) => {
    let stopped = false;
    let previous: string | undefined;
    let timer: ReturnType<typeof setTimeout>;
    let running = false;
    const poll = async () => {
      if (stopped || running) return;
      running = true;
      clearTimeout(timer);
      try {
        const snapshot = await nativePluginClient.getSettings(
          name,
          options.project,
          options.workspaceRoot,
        );
        const revision = JSON.stringify(snapshot);
        if (!stopped && revision !== previous) {
          previous = revision;
          listener(snapshot);
        }
      } catch (error) {
        if (!stopped) options.onError?.(error);
      } finally {
        running = false;
        if (!stopped) timer = setTimeout(() => void poll(), 2000);
      }
    };
    const changed = () => void poll();
    window.addEventListener('crowdex-plugins-changed', changed);
    void poll();
    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener('crowdex-plugins-changed', changed);
    };
  },
  list: listInstalledPlugins,
  install: (plugin: PluginCatalogItem) =>
    request<InstalledPlugin>({ method: 'install', params: { plugin } }),
  /**
   * Imports a folder of this machine. A `crowdex.json` folder answers what installing it would
   * grant until `grants` approves it.
   */
  installLocal: (path: string, grants?: readonly string[]) =>
    request<InstalledPlugin | LocalPluginPreview>({
      method: 'local.install',
      params: grants === undefined ? { path } : { path, grants: [...grants] },
    }),
  /** Approves what the waiting update of a plugin adds, and installs it. */
  approveUpdate: (name: string) =>
    request<InstalledPlugin>({ method: 'update.approve', params: { name } }),
  /** Uploads a folder plugin to the Crowdex Cloud account and installs it there. */
  publish: (name: string) => request<{ status: string }>({ method: 'publish', params: { name } }),
  readSource: (name: string, path: string, workspaceRoot?: string) =>
    request<{ content: string }>({
      method: 'source.read',
      params: { name, path, ...(workspaceRoot === undefined ? {} : { workspaceRoot }) },
    }),
  writeSource: (name: string, path: string, content: string, workspaceRoot?: string) =>
    request<PluginSourceWriteResult>({
      method: 'source.write',
      params: {
        name,
        path,
        content,
        ...(workspaceRoot === undefined ? {} : { workspaceRoot }),
      },
    }),
  /** Deletes skill folders of this computer (a `machineSkills` group's) and their lock entries. */
  removeMachineSkills: (folders: readonly string[]) =>
    request<{ removed: string[] }>({
      method: 'skills.machine.remove',
      params: { folders: [...folders] },
    }),
  uninstall: (name: string, workspaceRoot?: string) =>
    request<{ removed: true }>({
      method: 'uninstall',
      params: { name, ...(workspaceRoot === undefined ? {} : { workspaceRoot }) },
    }),
  setEnabled: (name: string, enabled: boolean, workspaceRoot?: string) =>
    request<InstalledPlugin>({ method: 'enabled.set', params: { name, enabled, workspaceRoot } }),
  connectorService: (service: string, workspaceRoot?: string) =>
    request<ConnectorServiceActions>({
      method: 'connectors.service',
      params: { service, ...(workspaceRoot === undefined ? {} : { workspaceRoot }) },
    }),
  /** What one action of a service does and takes; `null` when it cannot be described. */
  connectorAction: (service: string, action: string, workspaceRoot?: string) =>
    request<ConnectorActionDetail | null>({
      method: 'connectors.action',
      params: { service, action, ...(workspaceRoot === undefined ? {} : { workspaceRoot }) },
    }),
  /** The tools of one of a service's MCP servers, as operations, for a connected account. */
  connectorTools: (service: string, method: string, workspaceRoot?: string) =>
    request<ConnectorOperation[]>({
      method: 'connectors.tools',
      params: { service, method, ...(workspaceRoot === undefined ? {} : { workspaceRoot }) },
    }),
  setContributionEnabled: (name: string, key: string, enabled: boolean, workspaceRoot?: string) =>
    request<InstalledPlugin>({
      method: 'contributions.enabled.set',
      params: { name, key, enabled, ...(workspaceRoot === undefined ? {} : { workspaceRoot }) },
    }),
  /** Lets a command of a plugin run without a sandbox on this Windows machine, or stops it. */
  setCommandUnsandboxed: (name: string, command: string, allowed: boolean) =>
    request<InstalledPlugin>({
      method: 'commands.unsandboxed.set',
      params: { name, command, allowed },
    }),
  listPortableMcpServers: (workspaceRoot?: string) =>
    request<PluginMcpServer[]>({
      method: 'mcp.portable.list',
      params: workspaceRoot === undefined ? {} : { workspaceRoot },
    }),
  listMcpApps: (workspaceRoot?: string) =>
    request<Record<string, string>>({
      method: 'mcp.apps.list',
      params: workspaceRoot === undefined ? {} : { workspaceRoot },
    }),
  listMcpServerStatuses: (workspaceRoot?: string) =>
    request<McpServerStatus[]>({
      method: 'mcp.status',
      params: workspaceRoot === undefined ? {} : { workspaceRoot },
    }),
  reconnectMcpServer: (serverId: string, workspaceRoot?: string) =>
    request<McpServerStatus>({
      method: 'mcp.reconnect',
      params: { serverId, ...(workspaceRoot === undefined ? {} : { workspaceRoot }) },
    }),
  readMcpResource: (uri: string) =>
    request<{ uri: string; html: string }>({ method: 'mcp.resource.read', params: { uri } }),
  callMcpTool: (uri: string, name: string, args: Record<string, unknown>) =>
    request<Record<string, unknown>>({
      method: 'mcp.tools.call',
      params: { uri, name, arguments: args },
    }),
};
